import { DOCUMENT } from '@angular/common';
import { Injectable, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

export type AppLang = 'el' | 'en';

/**
 * App language: Greek (default) + English, runtime-switchable, persisted.
 * Greek is the market default; missing keys fall back to English (provider config).
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly translate = inject(TranslateService);
  private readonly doc = inject<Document>(DOCUMENT);
  private static readonly KEY = 'actively-lang';
  readonly supported: AppLang[] = ['el', 'en'];

  /** Current language as a signal so the switcher updates reactively. */
  readonly lang = signal<AppLang>('el');

  /**
   * Call once on app start, awaited by an app initializer so the active
   * language file is fully loaded before any component renders. That makes
   * `translate.instant(...)` safe to call synchronously in ngOnInit (SEO meta,
   * state-view messages, toasts) — otherwise it would return the raw key.
   */
  async init(): Promise<void> {
    this.translate.addLangs(this.supported);
    const lang = this.stored() ?? 'el';
    this.lang.set(lang);
    this.doc.documentElement.setAttribute('lang', lang);
    try {
      localStorage.setItem(LanguageService.KEY, lang);
    } catch {
      // localStorage may be unavailable (private mode) — non-fatal.
    }
    try {
      await firstValueFrom(this.translate.use(lang));
    } catch {
      // Never block bootstrap on a translation fetch failure; templates fall
      // back to the key / English.
    }
  }

  use(lang: AppLang): void {
    this.lang.set(lang);
    this.translate.use(lang);
    try {
      localStorage.setItem(LanguageService.KEY, lang);
    } catch {
      // localStorage may be unavailable (private mode) — non-fatal.
    }
    this.doc.documentElement.setAttribute('lang', lang);
  }

  toggle(): void {
    this.use(this.lang() === 'el' ? 'en' : 'el');
  }

  private stored(): AppLang | null {
    try {
      const v = localStorage.getItem(LanguageService.KEY);
      return v === 'el' || v === 'en' ? v : null;
    } catch {
      return null;
    }
  }
}
